import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Table,
  TableBody,
  AppBar,
  Toolbar,
  Typography,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Box,
  Paper,
  InputAdornment,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LoadingBar from "react-top-loading-bar";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import base64 from "base64-js";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const StockInward = () => {
  const [showUserGuide, setShowUserGuide] = useState(false);
  const navigate = useNavigate();
  const { encodedFormId } = useParams();
  // console.log("encodedFormId:", encodedFormId);

  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [stockInwardID, setStockInwardID] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [processingPrint, setProcessingPrint] = useState(false);

  // Decode the encodedFormId
  const decodeFormId = (encodedFormId) => {
    const bytes = base64.toByteArray(encodedFormId);
    return new TextDecoder().decode(bytes);
  };
  const formId = decodeFormId(encodedFormId);

  const fetchCategories = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      console.log(
        "Fetching from:",
        `${apiUrl}/inventory/Categories/GetMainCategory`
      );
      const response = await fetch(
        `${apiUrl}/inventory/InwardItem/GetStockInwardDetails`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        // console.log("Categories fetched:", responseData);
        setCategories(responseData || []);
        // console.log("Categories state updated:", responseData);
      } else {
        console.error("Failed to fetch categories:", response.status);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const fetchStockInward = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const searchQuery = searchTerm ? `?searchTerm=${searchTerm}` : "";
      // console.log("Fetching from:", `${apiUrl}/Inventory/InwardItem/ListStockInward${searchQuery}`);

      const response = await fetch(`${apiUrl}/Inventory/InwardItem/ListStockInward${searchQuery}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        // console.log("Stock Inward Data fetched:", responseData); // Log the API response
        setCategories(Array.isArray(responseData) ? responseData : []);// Update the state
        // console.log("Categories state updated:", responseData); // Log the updated state
      } else {
        console.error("Failed to fetch stock inward data:", response.status);
      }
    } catch (error) {
      console.error("Error fetching stock inward data:", error);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const printReceipt = async (id) => {
    console.log("printReceipt started, stockInwardID:", id);
    if (!id) {
      console.log("No stock inward ID, showing Snackbar");
      setSnackbarMessage("No stock inward ID available for printing");
      setSnackbarOpen(true);
      return;
    }

    setStockInwardID(id);
    setProcessingPrint(true);
    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");

      console.log("Attempting to fetch receipt data from API");
      const response = await fetch(
        `${apiUrl}/Inventory/InwardItem/GetStockInwardDetails/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      console.log("API response status:", response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API response data:", result);
      const { header, details } = result;

      // Create a hidden div to render the receipt
      console.log("Creating hidden div for rendering");
      const receiptDiv = document.createElement("div");
      receiptDiv.style.position = "absolute";
      receiptDiv.style.left = "-9999px";
      receiptDiv.style.width = "816px"; // Approx A4 width at 96dpi
      receiptDiv.innerHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Stock Inward Receipt</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { margin: 0; font-family: Arial, sans-serif; }
            .logo-placeholder { width: 80px; height: 80px; background: #e5e7eb; }
            @media print {
              body { background-color: #fff; }
              .max-w-3xl { max-width: none; }
              .shadow-lg { box-shadow: none; }
              .bg-gray-100 { background-color: #fff; }
            }
          </style>
        </head>
        <body class="font-sans bg-gray-100">
          <div class="max-w-3xl mx-auto bg-white p-8 my-8 rounded-lg shadow-lg">
            <div class="text-center text-sm text-gray-500 mb-4">
              STOCK INWARD RECEIPT
            </div>
            <!-- Header -->
            <div class="flex items-center justify-between mb-6 pb-4 border-b-2 border-blue-600">
              <div class="flex items-center">
                <div class="logo-placeholder rounded-full mr-4 border border-gray-300"></div>
                <div>
                  <h1 class="text-2xl font-bold text-blue-600">Stock Inward Receipt</h1>
                  <p class="text-sm text-gray-600 font-medium">Receipt No: ${header.receiptCode || "N/A"}</p>
                </div>
              </div>
              <div class="text-right bg-blue-50 p-3 rounded-lg border border-blue-100">
                <p class="text-sm text-gray-600">Received Date:</p>
                <p class="text-lg font-medium text-blue-700">${header.receivedDate || "N/A"}</p>
              </div>
            </div>
            <hr class="border-gray-300 mb-6">

            <!-- Received From -->
            <div class="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <h3 class="font-bold text-blue-600 mb-2">Vendor Details</h3>
                  <p class="text-gray-800">${header.vendorName || "N/A"}</p>
                </div>
                <div class="text-right">
                  <h3 class="font-bold text-blue-600 mb-2">Invoice Information</h3>
                  <p class="text-gray-800">Invoice: <span class="font-medium">${header.invoiceNumber || "N/A"}</span></p>
                  <p class="text-gray-800">Date: <span class="font-medium">${header.invoiceDate || "N/A"}</span></p>
                </div>
              </div>
            </div>

            <!-- Items Table -->
            <div class="mb-6 overflow-hidden rounded-lg border border-gray-200">
              <table class="w-full border-collapse">
                <thead>
                  <tr class="bg-blue-600 text-white">
                    <th class="border border-blue-700 p-3 text-left">Item Code</th>
                    <th class="border border-blue-700 p-3 text-left">Item Name</th>
                    <th class="border border-blue-700 p-3 text-right">Qty</th>
                    <th class="border border-blue-700 p-3 text-right">Unit Cost</th>
                    <th class="border border-blue-700 p-3 text-right">Tax %</th>
                    <th class="border border-blue-700 p-3 text-right">Tax Amount</th>
                    <th class="border border-blue-700 p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${details
          .map(
            (item, index) => `
                    <tr class="${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50">
                      <td class="border border-gray-200 p-3 font-medium">${item.itemCode || "N/A"}</td>
                      <td class="border border-gray-200 p-3">${item.itemName || "N/A"}</td>
                      <td class="border border-gray-200 p-3 text-right">${item.quantityReceived || 0}</td>
                      <td class="border border-gray-200 p-3 text-right">${item.unitCost || 0}</td>
                      <td class="border border-gray-200 p-3 text-right">${item.taxRate || 0}</td>
                      <td class="border border-gray-200 p-3 text-right">${item.taxAmount || 0}</td>
                      <td class="border border-gray-200 p-3 text-right font-medium">${item.amount || 0}</td>
                    </tr>
                  `
          )
          .join("")}
                </tbody>
              </table>
            </div>

            <!-- Summary -->
            <div class="flex justify-end mb-6">
              <div class="w-1/3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 class="font-bold text-blue-600 mb-2 text-center">SUMMARY</h3>
                <div class="flex justify-between border-t border-gray-300 pt-2 mb-1">
                  <p class="font-medium">Total Quantity:</p>
                  <p class="font-bold">${header.totalQuantity || 0}</p>
                </div>
                <div class="flex justify-between mb-1">
                  <p class="font-medium">Total Tax Amount:</p>
                  <p class="font-bold">${header.totalTaxAmount || 0}</p>
                </div>
                <div class="flex justify-between font-bold text-blue-600 border-t border-gray-300 pt-2 mt-2">
                  <p>Total Amount:</p>
                  <p>${header.totalAmount || 0}</p>
                </div>
              </div>
            </div>

            <!-- Created By -->
            <div class="text-sm text-gray-500 text-right mb-6">
              <p>Receipt generated on: ${header.createdDate ? header.createdDate.split(' ')[0] : "N/A"}</p>
            </div>

            <!-- Footer -->
            <hr class="border-gray-300 mb-6">
            <div class="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p class="font-bold text-gray-700">Authorized Signature:</p>
                <div class="h-12 border-b border-gray-400 w-48 mt-6"></div>
              </div>
              <div class="text-right">
                <p class="font-bold text-gray-700">Store Stamp:</p>
                <div class="h-20 w-20 border border-dashed border-gray-400 rounded-full ml-auto mt-2"></div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;
      document.body.appendChild(receiptDiv);
      console.log("Hidden div appended to document");

      // Wait for Tailwind CSS to load and render
      console.log("Waiting for Tailwind CSS to render");
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Convert the div to canvas
      console.log("Converting div to canvas with html2canvas");
      const canvas = await html2canvas(receiptDiv, {
        scale: 2, // Higher resolution for better quality
        useCORS: true,
        logging: false,
      });

      // Remove the temporary div
      console.log("Removing temporary div");
      document.body.removeChild(receiptDiv);

      // Create PDF
      console.log("Creating PDF with jsPDF");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

      // Generate blob and open in new tab
      console.log("Generating PDF blob and opening in new tab");
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pdfWindow = window.open(pdfUrl, "_blank");

      // Trigger print dialog after PDF loads
      if (pdfWindow) {
        console.log("PDF window opened, setting onload print");
        pdfWindow.onload = () => {
          pdfWindow.print();
        };
      } else {
        console.warn(
          "Failed to open PDF window, possibly blocked by popup blocker"
        );
        setSnackbarMessage("Please allow popups to view the PDF receipt");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error in printReceipt:", error);
      setSnackbarMessage("Failed to generate PDF receipt");
      setSnackbarOpen(true);
    } finally {
      console.log("printReceipt completed, setting isLoading to false");
      setIsLoading(false);
      setProcessingPrint(false);
    }
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem("token");
      // console.log("Sending decoded formId:", formId);

      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData);
        const authorizationData = responseData[0];
        setCanDelete(authorizationData.uDelete === 1);
        setCanEdit(authorizationData.uModify === 1);
        setLoadingBarProgress(100);
      } else {
        console.error("Authorization failed:", response.status);
        alert("Invalid authorization response");
        setLoadingBarProgress(0);
      }
    } catch (error) {
      console.error("API request error:", error);
      alert("An error occurred. Please try again later.");
      setLoadingBarProgress(0);
    }
  };

  // First, add a new function to handle invoice downloads
  const downloadInvoiceCopy = async (id) => {
    console.log("downloadInvoiceCopy started, stockInwardID:", id);
    if (!id) {
      setSnackbarMessage("No stock inward ID available for invoice");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");

      const response = await fetch(
        `${apiUrl}/Inventory/InwardItem/GetStockInwardDetails/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const { header } = result;

      // Check if invoiceCopy exists in the header
      if (header.invoiceCopy) {
        // Determine file type and create appropriate blob
        let fileData, fileType, fileName;

        // Check if it's a base64 string
        if (header.invoiceCopy.startsWith('data:')) {
          // Parse the MIME type and base64 data
          const matches = header.invoiceCopy.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            fileType = matches[1];
            fileData = atob(matches[2]);
          } else {
            throw new Error("Invalid base64 format");
          }
        } else {
          // Assume it's a plain base64 string without data URL format
          fileData = atob(header.invoiceCopy);
          // Try to determine file type from first few bytes (simple check)
          const uint8Array = new Uint8Array(fileData.length);
          for (let i = 0; i < fileData.length; i++) {
            uint8Array[i] = fileData.charCodeAt(i);
          }
          const headerBytes = uint8Array.slice(0, 4);

          // Check file signature
          if (headerBytes[0] === 0x25 && headerBytes[1] === 0x50 && headerBytes[2] === 0x44 && headerBytes[3] === 0x46) {
            fileType = "application/pdf";
            fileName = `invoice_${header.receiptCode || id}.pdf`;
          } else if (headerBytes[0] === 0xFF && headerBytes[1] === 0xD8) {
            fileType = "image/jpeg";
            fileName = `invoice_${header.receiptCode || id}.jpg`;
          } else if (headerBytes[0] === 0x89 && headerBytes[1] === 0x50 && headerBytes[2] === 0x4E && headerBytes[3] === 0x47) {
            fileType = "image/png";
            fileName = `invoice_${header.receiptCode || id}.png`;
          } else {
            // Default to PDF if can't determine
            fileType = "application/pdf";
            fileName = `invoice_${header.receiptCode || id}.pdf`;
          }
        }

        // Create a blob from the binary data
        const byteCharacters = fileData;
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: fileType });

        // Create download link and trigger download
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName || `invoice_${header.receiptCode || id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbarMessage("Invoice downloaded successfully");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("No invoice copy available for this stock inward");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setSnackbarMessage("Failed to download invoice copy");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = () => {
    navigate("/stockinward/add", { state: { encodedFormId } });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    // console.log("Component mounted, fetching data...");
    fetchStockInward(); // Call the new API
    Authorizer();
  }, []);

  const filteredCategories = categories.filter((category) =>
    category.receiptCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.vendorName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="container ">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{fontWeight:'bold'}}>
            Stock Inward Form  <InfoIcon onClick={() => setShowUserGuide(true)} style={{ "opacity": "0.6" }} />
          </Typography>

          <Dialog
            open={showUserGuide}
            onClose={() => setShowUserGuide(false)}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                py: 2,
              }}
            >
              Fee Deposit User Guide
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1898c2",
                  fontWeight: "600",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                Quick Steps to Use Stock Inward Forms
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: "none",
                  padding: 0,
                  "& li": {
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    fontSize: "0.9rem",
                    color: "#333",
                  },
                  "& li::before": {
                    content: '"\\2022"', // Bullet point
                    color: "#1898c2",
                    fontWeight: "bold",
                    display: "inline-block",
                    width: "1.5em",
                    mr: 1,
                    fontSize: "1.2rem",
                  },
                  "& strong": {
                    margin: "0 0.2em", // Add spacing around strong tags
                  },
                }}
              >
                <li><strong>Search</strong> stock entries by receipt code or vendor</li>
                <li><strong>Add new</strong> stock entries with "Add Stock" button</li>
                <li><strong>View</strong> receipt details in the table</li>
                <li><strong>Print</strong> receipts for any stock entry</li>
                <li>Download original invoice copies by clicking <strong>"INVOICE COPIES"</strong> button</li>
                <li>Use <strong>pagination</strong> to navigate large lists</li>
                <li>Check <strong>total amounts</strong> for each entry</li>

              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <StyledButton
                onClick={() => setShowUserGuide(false)}
                color="primary"
                variant="contained"
                sx={{
                  backgroundColor: "#1898c2",
                  "&:hover": { backgroundColor: "#156f9a" },
                  borderRadius: "8px",
                  px: 4,
                }}
              >
                Close
              </StyledButton>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, marginTop: 6 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search Stock..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, backgroundColor: "#f9f9f9" }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddCategory}
          >
            Add Stock
          </Button>
        </div>

        {isLoading ? (
          <Typography>Loading Stock...</Typography>
        ) : (
          <>
            <TableContainer component={Paper} style={{  marginTop: 16 }}>
  <Table stickyHeader sx={{
    minWidth: 650,
    border: '1px solid #ccc',
    '& .MuiTableCell-root': {
      borderRight: '1px solid #ccc',
      fontSize: '1rem',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
    },
  }}>
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          Receipt Code
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          Vendor Name
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "15%" }}>
          Total Amount
        </TableCell>
        <TableCell sx={{textAlign:'center', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          View Receipt
        </TableCell>
        <TableCell sx={{textAlign:'center', borderBottom: '1px solid #ccc', borderRight: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', width: "20%" }}>
          Download Invoice
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {paginatedCategories.length > 0 ? (
        paginatedCategories.map((category) => (
          <TableRow key={category.stockInwardID} sx={{ borderTop: '1px solid #ccc' }}>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', whiteSpace: "normal", wordWrap: "break-word" }}>
              {category.receiptCode || "N/A"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', whiteSpace: "normal", wordWrap: "break-word" }}>
              {category.vendorName || "N/A"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
              {category.totalAmount || "N/A"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', textAlign: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => printReceipt(category.stockInwardID)}
                disabled={processingPrint && stockInwardID === category.stockInwardID}
                // sx={{ borderRadius: "4px", backgroundColor: "#986AB6" }}
              >
                {processingPrint && stockInwardID === category.stockInwardID ? "Processing..." : "Print"}
              </Button>
            </TableCell>
            <TableCell sx={{ fontSize: '1.1rem', textAlign: 'center' }}>
              <Button
                variant="contained"
                color="warning"
                onClick={() => downloadInvoiceCopy(category.stockInwardID)}
                disabled={isLoading}
                // sx={{ borderRadius: "4px", backgroundColor: "#0B1F3D" }}
              >
                Invoice Copy
              </Button>
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={5} sx={{ textAlign: 'center' }}>No stock inward data available</TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCategories.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StockInward;